import { useState, useEffect, useRef } from "react";
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from "react-router-dom";
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import {
  UserPlus, Search, MessageCircle, FileCheck, DollarSign,
  GraduationCap, Users, ArrowRight, Sparkles, CheckCircle,
  Target, Zap, Star, Shield, Video, CalendarCheck, Award,
  BookOpen, TrendingUp, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { cn } from "@/lib/utils";
import { getCognitoSignupUrl } from "@/lib/auth";

/* ── Scroll reveal ── */
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("visible"); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

/* ── Data ── */
const studentSteps = [
  {
    icon: Target,
    title: "Tell Us What You Need",
    description: "Tell us your subject, level, and goals. We'll show you teachers who are the right fit — not just available.",
    gradient: "from-[hsl(39,96%,48%)] to-[hsl(30,90%,45%)]",
    shadow: "shadow-[hsl(39,96%,48%)]/25",
    detail: ["Takes under 5 minutes", "No credit card required", "Covers multiple subjects"],
  },
  {
    icon: Zap,
    title: "Browse Matched Teachers",
    description: "Review verified profiles, read about each teacher's approach and teaching style, and reach out directly. No gatekeepers.",
    gradient: "from-[hsl(260,60%,55%)] to-[hsl(280,55%,50%)]",
    shadow: "shadow-[hsl(260,60%,55%)]/25",
    detail: ["Every teacher admin-verified", "Browse full teacher profiles", "Read verified reviews"],
  },
  {
    icon: MessageCircle,
    title: "Send an Enquiry",
    description: "Found a teacher you like? Send an inquiry with your goals and availability. Teachers respond directly through the platform.",
    gradient: "from-[hsl(35,95%,55%)] to-[hsl(25,90%,50%)]",
    shadow: "shadow-[hsl(35,95%,55%)]/25",
    detail: ["In-platform messaging", "Share your availability", "Direct teacher communication"],
  },
  {
    icon: Video,
    title: "Book and Begin",
    description: "Pick a time, confirm your booking, and show up. Your teacher handles the rest — session prep, materials, and follow-up.",
    gradient: "from-[hsl(340,70%,55%)] to-[hsl(310,60%,50%)]",
    shadow: "shadow-[hsl(340,70%,55%)]/25",
    detail: ["Integrated video sessions", "Rate your teacher after", "Book more when ready"],
  },
];

const coachSteps = [
  {
    icon: UserPlus,
    title: "Build Your Profile",
    description: "Create your verified teacher profile — your subjects, experience, teaching approach, and pricing. Your profile is your storefront on the platform.",
    gradient: "from-[hsl(39,96%,48%)] to-[hsl(30,90%,45%)]",
    shadow: "shadow-[hsl(39,96%,48%)]/25",
    detail: ["Free to apply", "Full profile control", "Set your own rates"],
  },
  {
    icon: Shield,
    title: "Get Admin-Approved",
    description: "Our team reviews your profile before it goes live — identity, qualifications, and teaching approach. This keeps the platform trusted and your credibility visible.",
    gradient: "from-[hsl(260,60%,55%)] to-[hsl(280,55%,50%)]",
    shadow: "shadow-[hsl(260,60%,55%)]/25",
    detail: ["Identity & credential check", "Profile review before going live", "Verified badge on profile"],
  },
  {
    icon: BookOpen,
    title: "Publish Your Offerings",
    description: "List 1-on-1 sessions, group cohorts, or structured crash courses. Set your schedule, define what students will learn, and publish when ready.",
    gradient: "from-[hsl(35,95%,55%)] to-[hsl(25,90%,50%)]",
    shadow: "shadow-[hsl(35,95%,55%)]/25",
    detail: ["1-on-1, group & crash courses", "Flexible scheduling", "INR & USD pricing"],
  },
  {
    icon: DollarSign,
    title: "Start Teaching",
    description: "Accept student bookings, show up to your sessions, and build your student base at your own pace. Earnings are released after each completed session.",
    gradient: "from-[hsl(152,60%,42%)] to-[hsl(152,55%,35%)]",
    shadow: "shadow-[hsl(152,60%,42%)]/25",
    detail: ["Transparent fee structure", "Grow your student base", "Teach on your schedule"],
  },
];

const platformFeatures = [
  { icon: Shield, title: "All teachers verified", desc: "Identity, credentials & teaching demo reviewed before going live" },
  { icon: Video, title: "Built-in video sessions", desc: "No external tools needed — sessions run directly on the platform" },
  { icon: Star, title: "Verified reviews only", desc: "Only students who completed a session can leave a review" },
  { icon: CalendarCheck, title: "Smart scheduling", desc: "Timezone-aware booking with automatic reminders for both parties" },
  { icon: Award, title: "Smart search & filtering", desc: "Browse by subject, level, teaching style, and availability to find exactly the right fit." },
  { icon: TrendingUp, title: "Progress tracking", desc: "Students and teachers can log milestones and track learning outcomes" },
];

const faqs = [
  { q: "Is it free to sign up as a student?", a: "Yes — creating a student account and browsing teachers is completely free. You only pay when you book a session." },
  { q: "How are teachers screened?", a: "Every teacher undergoes identity verification, qualification review, and a teaching demo assessment before their profile goes live. They also earn a Verified badge displayed on their profile." },
  { q: "What if I'm not happy with my session?", a: "Raise a dispute within 48 hours of the session end time. Our support team reviews evidence from both sides and resolves it within 5 business days, issuing a refund where warranted." },
  { q: "Can I try a teacher before committing to multiple sessions?", a: "Absolutely. You can book a single session with any teacher before deciding to continue. Many teachers also offer a discounted first-session rate." },
  { q: "How does payment work for teachers?", a: "Teacher earnings (net of platform fees) are held in escrow and released within 3 business days after session completion, directly to your registered bank account." },
  { q: "What subjects and topics are supported?", a: "We currently support 12+ subject areas including Mathematics, Science, English, Programming, Music, Art & Design, Languages, Fitness, and more. New categories are added regularly." },
];

export default function HowItWorksPage() {
  const heroRef = useReveal<HTMLDivElement>();
  const featuresRef = useReveal<HTMLDivElement>();
  const faqRef = useReveal<HTMLDivElement>();
  const ctaRef = useReveal<HTMLDivElement>();
  const { pathname } = useLocation();
  const [activeTab, setActiveTab] = useState<"student" | "coach">("student");
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const steps = activeTab === "student" ? studentSteps : coachSteps;

  const title = 'How It Works | BookMyCoaching';
  const description = 'Learn how BookMyCoaching connects students with verified tutors for one-to-one and group coaching.';
  const canonicalUrl = getCanonicalUrl(pathname);
  const ogImage = getDefaultOgImage();

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
      </Helmet>
      <Header />
      <main>

        {/* ── Hero ── */}
        <section className="relative pt-28 pb-10 sm:pt-44 sm:pb-20 overflow-hidden">
          {/* Indigo gradient background */}
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(237,52%,92%)]/80 via-[hsl(237,45%,90%)]/60 to-[hsl(39,96%,93%)]/50" />
          {/* Subtle grid overlay */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hiw-hero-grid" x="0" y="0" width="44" height="44" patternUnits="userSpaceOnUse">
                <path d="M 44 0 L 0 0 0 44" fill="none" stroke="hsl(237,52%,31%)" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hiw-hero-grid)" />
          </svg>
          <div className="hidden sm:block absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-24 -right-24 w-[500px] h-[500px] bg-white/20 rounded-full blur-[120px]" />
            <div className="absolute -bottom-32 -left-24 w-[400px] h-[400px] bg-[hsl(237,52%,31%)]/10 rounded-full blur-[100px]" />
          </div>

          <div ref={heroRef} className="container mx-auto px-4 relative z-10 text-center reveal">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-[hsl(237,52%,31%)] mb-4 px-3 py-1 rounded-full bg-white/80 border border-[hsl(237,52%,31%)]/20 backdrop-blur-sm">
              <Sparkles className="w-3 h-3" />
              Simple by design
            </span>
            <h1 className="font-sans text-[1.75rem] sm:text-5xl lg:text-6xl text-[#262A7A] mb-4 leading-tight">
              Getting started is{" "}
              <span className="relative inline-block text-[hsl(237,52%,31%)]">
                simple
                <svg className="absolute -bottom-1 sm:-bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none" aria-hidden="true">
                  <path d="M4 9 C 50 3, 150 3, 196 9" stroke="hsl(39,96%,48%)" strokeWidth="4" strokeLinecap="round" />
                </svg>
              </span>
            </h1>
            <p className="text-[#5A5E80] text-sm sm:text-lg max-w-xl mx-auto leading-relaxed mb-7">
              Four straightforward steps — whether you're here to learn something new or share what you know.
            </p>

            {/* Persona toggle */}
            <div className="inline-flex items-center rounded-full bg-white/40 backdrop-blur-md p-1.5 border border-white/60 shadow-sm">
              <button
                onClick={() => setActiveTab("student")}
                className={cn(
                  "flex items-center gap-2 px-5 sm:px-6 py-2 sm:py-2.5 rounded-full text-sm font-semibold transition-all duration-300 touch-manipulation active:scale-[0.97]",
                  activeTab === "student"
                    ? "bg-[hsl(237,52%,31%)] text-white shadow-lg"
                    : "text-[hsl(237,52%,40%)] hover:text-[hsl(237,52%,31%)]"
                )}
              >
                <GraduationCap className="w-4 h-4" />
                I'm a Student
              </button>
              <button
                onClick={() => setActiveTab("coach")}
                className={cn(
                  "flex items-center gap-2 px-5 sm:px-6 py-2 sm:py-2.5 rounded-full text-sm font-semibold transition-all duration-300 touch-manipulation active:scale-[0.97]",
                  activeTab === "coach"
                    ? "bg-[hsl(237,52%,31%)] text-white shadow-lg"
                    : "text-[hsl(237,52%,40%)] hover:text-[hsl(237,52%,31%)]"
                )}
              >
                <Users className="w-4 h-4" />
                I'm a Teacher
              </button>
            </div>
          </div>
        </section>

        {/* ── Steps ── */}
        <section className="py-12 sm:py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">

              {/* Mobile: native grouped list */}
              <div className="sm:hidden bg-white rounded-2xl overflow-hidden border border-[#DCDDEE] shadow-sm mb-8">
                {steps.map((step, i) => {
                  const Icon = step.icon;
                  return (
                    <div key={`${activeTab}-${i}`} className={`flex items-start gap-4 px-4 py-4 ${i < steps.length - 1 ? 'border-b border-[#DCDDEE]' : ''}`}>
                      <div className={cn("w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center shrink-0", step.gradient)}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#A9ACD6]">Step {i + 1}</span>
                        <p className="font-semibold text-[#262A7A] text-sm leading-tight">{step.title}</p>
                        <p className="text-xs text-[#5A5E80] mt-0.5 leading-relaxed">{step.description}</p>
                        <div className="flex flex-wrap gap-x-3 mt-2">
                          {step.detail.map((d, j) => (
                            <span key={j} className="flex items-center gap-1 text-[10px] text-[hsl(237,52%,40%)] font-medium">
                              <CheckCircle className="w-3 h-3 shrink-0" />
                              {d}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop: card layout */}
              <div className="hidden sm:block relative">
                {/* Vertical connector line */}
                <div className="absolute left-[2.75rem] top-16 bottom-16 w-px bg-gradient-to-b from-[hsl(237,52%,40%)]/40 via-[hsl(237,52%,40%)]/20 to-transparent z-0" />
                <div className="flex flex-col gap-6">
                  {steps.map((step, i) => (
                    <div
                      key={`${activeTab}-${i}`}
                      className="relative flex gap-6 items-start group"
                    >
                      <div className="flex-shrink-0 z-10">
                        <div className={cn(
                          "w-[5.5rem] h-[5.5rem] rounded-2xl flex flex-col items-center justify-center bg-gradient-to-br shadow-lg relative",
                          step.gradient, step.shadow
                        )}>
                          <step.icon className="w-6 h-6 text-white mb-1" />
                          <span className="text-xs font-bold text-white/70 uppercase tracking-widest">Step {i + 1}</span>
                        </div>
                      </div>
                      <div className="flex-1 rounded-2xl border border-border/40 bg-card p-7 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 group-hover:border-border/70">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="font-sans text-xl text-foreground mb-2 group-hover:text-[hsl(237,52%,31%)] transition-colors">
                              {step.title}
                            </h3>
                            <p className="text-muted-foreground text-sm leading-relaxed">
                              {step.description}
                            </p>
                          </div>
                          <div className="flex flex-col gap-1.5 sm:w-48 flex-shrink-0">
                            {step.detail.map((d, j) => (
                              <div key={j} className="flex items-center gap-2">
                                <CheckCircle className="w-3.5 h-3.5 text-[hsl(237,52%,40%)] flex-shrink-0" />
                                <span className="text-xs text-muted-foreground">{d}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA below steps */}
              <div className="mt-8 sm:mt-12 hidden sm:flex flex-col sm:flex-row gap-4 justify-center items-center">
                {activeTab === "student" ? (
                  <>
                    <Button asChild size="lg" className="rounded-full px-8 bg-[hsl(237,52%,31%)] hover:bg-[hsl(237,52%,20%)] text-white shadow-xl shadow-[hsl(237,52%,31%)]/25 hover:-translate-y-0.5 hover:shadow-2xl transition-all">
                      <Link to="/search">
                        Find Your Teacher
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                    <Button size="lg" variant="outline" className="rounded-full px-8">
                      <a href={getCognitoSignupUrl()}>Create Free Account</a>
                    </Button>
                  </>
                ) : (
                  <>
                    <Button asChild size="lg" className="rounded-full px-8 bg-[hsl(237,52%,31%)] hover:bg-[hsl(237,52%,20%)] text-white shadow-xl shadow-[hsl(237,52%,31%)]/25 hover:-translate-y-0.5 hover:shadow-2xl transition-all">
                      <Link to="/teacher-profile-setup">
                        Apply as a Teacher
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                    <Button asChild size="lg" variant="outline" className="rounded-full px-8">
                      <Link to="/how-it-works/verification">Learn About Verification</Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── Platform features ── */}
        <section className="py-12 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <div ref={featuresRef} className="reveal">
              <div className="text-center mb-8 sm:mb-14 max-w-2xl mx-auto">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-[hsl(237,52%,40%)] mb-3 px-3 py-1 rounded-full bg-[hsl(237,52%,31%)]/10">
                  <Sparkles className="w-3 h-3" />
                  Built for both sides
                </span>
                <h2 className="font-sans text-2xl sm:text-4xl text-foreground mb-3">
                  Everything you need in one place
                </h2>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  BookMyCoaching handles the infrastructure so students can focus on learning and teachers can focus on teaching.
                </p>
              </div>

              {/* Mobile: horizontal scroll */}
              <div className="sm:hidden overflow-x-auto no-scrollbar px-4 -mx-4">
                <div className="inline-flex gap-3 px-4 pb-2" style={{ minWidth: 'max-content' }}>
                  {platformFeatures.map((f, i) => (
                    <div key={i} className="flex flex-col gap-3 p-4 rounded-2xl bg-white border border-border/40 w-[200px]">
                      <div className="w-9 h-9 rounded-xl bg-[hsl(237,52%,31%)]/10 flex items-center justify-center shrink-0">
                        <f.icon className="w-4 h-4 text-[hsl(237,52%,40%)]" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground text-sm mb-1">{f.title}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Desktop: grid */}
              <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
                {platformFeatures.map((f, i) => (
                  <div key={i} className="flex gap-4 p-6 rounded-2xl bg-card border border-border/40 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
                    <div className="w-10 h-10 rounded-xl bg-[hsl(237,52%,31%)]/10 flex items-center justify-center flex-shrink-0">
                      <f.icon className="w-5 h-5 text-[hsl(237,52%,40%)]" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground text-sm mb-1">{f.title}</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Stats strip ── */}
        <section className="py-12 sm:py-16 bg-gradient-to-r from-[hsl(237,52%,25%)] to-[hsl(237,52%,31%)] relative overflow-hidden">
          <div className="hidden sm:block absolute inset-0 opacity-[0.04]">
            <svg className="w-full h-full">
              <defs>
                <pattern id="hiw-dots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
                  <circle cx="2" cy="2" r="1" fill="white" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#hiw-dots)" />
            </svg>
          </div>
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-4xl mx-auto text-center">
              {[
                { value: "Free", label: "To browse and find a teacher", icon: Clock },
                { value: "Verified", label: "Every teacher is admin-reviewed", icon: Shield },
                { value: "1-on-1", label: "Or group — you choose the format", icon: Star },
                { value: "Fast", label: "Teacher payout after each session", icon: DollarSign },
              ].map((s, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <s.icon className="w-5 h-5 text-amber-300 mb-1" />
                  <p className="text-xl sm:text-3xl font-bold text-white">{s.value}</p>
                  <p className="text-[11px] sm:text-xs text-indigo-200 leading-tight">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="py-12 sm:py-24 bg-background">
          <div className="container mx-auto px-4 max-w-3xl">
            <div ref={faqRef} className="reveal">
              <div className="text-center mb-8 sm:mb-12">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-[hsl(237,52%,40%)] mb-3 px-3 py-1 rounded-full bg-[hsl(237,52%,31%)]/10">
                  <Sparkles className="w-3 h-3" />
                  Common questions
                </span>
                <h2 className="font-sans text-2xl sm:text-4xl text-foreground mb-2">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">Still have questions? <Link to="/contact" className="text-[hsl(237,52%,40%)] hover:underline underline-offset-4">Contact our team →</Link></p>
              </div>

              <div className="flex flex-col gap-2 sm:gap-3">
                {faqs.map((faq, i) => (
                  <div key={i} className="rounded-2xl border border-border/40 bg-card overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between gap-4 px-4 sm:px-6 py-4 text-left hover:bg-muted/30 active:bg-muted/50 touch-manipulation transition-colors"
                    >
                      <span className="text-sm font-semibold text-foreground">{faq.q}</span>
                      <span className={cn(
                        "w-5 h-5 rounded-full border border-border flex items-center justify-center flex-shrink-0 transition-transform duration-300",
                        openFaq === i ? "rotate-45 border-[hsl(237,52%,40%)]" : ""
                      )}>
                        <ArrowRight className={cn("w-3 h-3 transition-colors", openFaq === i ? "text-[hsl(237,52%,40%)]" : "text-muted-foreground")} />
                      </span>
                    </button>
                    {openFaq === i && (
                      <div className="px-4 sm:px-6 pb-4 sm:pb-5 text-sm text-muted-foreground leading-relaxed border-t border-border/40 pt-3 sm:pt-4">
                        {faq.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-12 sm:py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(237,52%,25%)] via-[hsl(237,52%,31%)] to-[hsl(237,52%,20%)]" />
          <div className="hidden sm:block absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-24 -right-24 w-[500px] h-[500px] bg-white/20 rounded-full blur-[120px]" />
            <div className="absolute -bottom-32 -left-24 w-[400px] h-[400px] bg-[hsl(237,52%,31%)]/10 rounded-full blur-[100px]" />
          </div>

          <div ref={ctaRef} className="container mx-auto px-4 relative z-10 text-center reveal">
            <div className="max-w-2xl mx-auto">
              <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/40 border border-white/60 text-white text-sm font-semibold mb-6 backdrop-blur-md">
                <Sparkles className="w-3.5 h-3.5" />
                Ready when you are
              </span>
              <h2 className="font-sans text-3xl sm:text-5xl text-white mb-4">
                Start your journey{" "}
                <span className="text-white drop-shadow-sm">today</span>
              </h2>
              <p className="text-indigo-200 text-base sm:text-lg mb-8 leading-relaxed">
                Free to browse. Verified teachers. Your pace, your schedule.
              </p>
              <div className="hidden sm:flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="rounded-full px-10 py-6 bg-[hsl(237,52%,31%)] hover:bg-[hsl(237,52%,20%)] text-white shadow-xl shadow-[hsl(237,52%,31%)]/25 hover:-translate-y-0.5 hover:shadow-2xl transition-all">
                  <Link to="/search">
                    Find a Teacher — it's free
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button asChild size="lg" className="rounded-full px-10 py-6 bg-white/50 border border-white/80 text-[hsl(237,52%,31%)] hover:bg-white/70 backdrop-blur-sm">
                  <Link to="/teacher-profile-setup">Apply as a Teacher</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* Bottom spacer so content clears the sticky CTA bar on mobile */}
      <div className="sm:hidden h-20" />
      <Footer />

      {/* ── Sticky bottom CTA (mobile only) ── */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-t border-[#DCDDEE] px-4 py-3 shadow-[0_-8px_32px_rgba(0,0,0,0.08)]">
        <div className="flex gap-3">
          {activeTab === "student" ? (
            <>
              <Link
                to="/search"
                className="touch-manipulation flex-1 bg-[hsl(237,52%,31%)] text-white font-bold text-sm py-4 rounded-2xl active:bg-[hsl(237,52%,22%)] active:scale-[0.98] transition-all shadow-lg shadow-[hsl(237,52%,31%)]/30 flex items-center justify-center"
              >
                Find a Teacher
              </Link>
              <a
                href={getCognitoSignupUrl()}
                className="touch-manipulation flex-1 bg-[#EEEFF9] text-[#262A7A] font-bold text-sm py-4 rounded-2xl active:bg-slate-200 active:scale-[0.98] transition-all flex items-center justify-center"
              >
                Sign up free
              </a>
            </>
          ) : (
            <>
              <Link
                to="/teacher-profile-setup"
                className="touch-manipulation flex-1 bg-[hsl(237,52%,31%)] text-white font-bold text-sm py-4 rounded-2xl active:bg-[hsl(237,52%,22%)] active:scale-[0.98] transition-all shadow-lg shadow-[hsl(237,52%,31%)]/30 flex items-center justify-center"
              >
                Apply as Teacher
              </Link>
              <Link
                to="/how-it-works/verification"
                className="touch-manipulation flex-1 bg-[#EEEFF9] text-[#262A7A] font-bold text-sm py-4 rounded-2xl active:bg-slate-200 active:scale-[0.98] transition-all flex items-center justify-center"
              >
                Verification →
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
